// MA-784 — restore the diagnostic half of a child's start-module PROVENANCE
// from the cloud when a device loads the child.
//
// Problem (MA-775 RED S2): `progress.moduleProvenance` is device-local. A
// fresh device (or a cleared browser) has no entry, so the legacy rule in
// useTodaysMission.isEvidenceFloor reads any floor that equals some band's
// seed as an AGE seed — and a diagnostic-raised floor that happens to equal a
// seed (math.09/10/11, reading.06/09/11) loses its exemption and its pick.
//
// What the cloud DOES carry (no schema change): the onboarding diagnostic's
// two `placement_results` rows (one per strand; `result.source`,
// `result.finalRung`, `result.total`) and the current levels
// (students.math_level_id / reading_level_id, merged into progress by App.jsx).
// This module turns "the newest stored diagnostic of this strand resolved to
// exactly this child's current module" into the SAME local entry the writing
// device recorded ({moduleId, source: "diagnostic"}).
//
// What the cloud does NOT carry: mastery moves. There is no cloud-carried
// source for a floor that mastery/walkback moved (and no production caller of
// computeEngineModuleAdvances today), so that half of MA-784 is NOT restored
// here — see docs/build/reports/MA-784-builder-baseline-2026-10-05.md.
//
// Mirrors isEvidenceFloor's own order so a restored entry never classifies a
// floor more generously than the legacy rule did for a device that held the
// stored diagnostic: a floor equal to THIS age's seed is age (step 2 comes
// before the diagnostic step 3), so it is never restored as evidence.
//
// Rules: pure planning + a fail-safe async wrapper. Never overwrites ANY
// existing local entry (current or stale = newer local knowledge); keyed per
// child (the apply goes through the child-scoped ctx and a link check); a
// network error or a malformed row changes nothing (legacy rule stays).

import { diagnosticResultRung } from "@/engine/diagnosticResultRung";
import {
  ageSeedModules,
  hasModuleProvenanceEntry,
  recordedModuleSource,
  type ModuleProvenance,
  type ModuleStrand,
} from "./progress.ts";
import {
  checkInOutcome,
  checkInTime,
  DIAGNOSTIC_RUNG_MODULE_IDS,
  newestCheckIn,
  validCloudCheckIn,
  type CloudCheckIn,
  type CloudCheckIns,
} from "../lib/engine/evidenceFloor.ts";
import { mathModuleIdForRung } from "../engine/mathDiagnosticStaircase.ts";
import { readingModuleIdForRung } from "../engine/readingDiagnosticContent.ts";

export interface PlacementResultRow {
  result?: unknown;
  source?: unknown;
  created_at?: unknown;
}

interface ProgressLike {
  mathModuleId?: string | null;
  readingModuleId?: string | null;
  studentAge?: number | null;
  parentPriors?: unknown;
  moduleProvenance?: ModuleProvenance | null;
  placementResults?: unknown;
  readingPlacementResults?: unknown;
  cloudCheckIn?: CloudCheckIns | null;
  [k: string]: unknown;
}

const STRANDS: ModuleStrand[] = ["math", "reading"];
const RESULT_TAG: Record<ModuleStrand, string> = {
  math: "math_diagnostic_staircase",
  reading: "reading_diagnostic_staircase",
};

/** The module a stored diagnostic row resolved to (its placement rung; a
 *  pre-MA-1120 row: its terminal rung), or
 *  null when the row is not a usable diagnostic of `strand`. Same validity
 *  test as useTodaysMission's local `diagnosticModule`. */
export function diagnosticModuleFromRow(strand: ModuleStrand, row: PlacementResultRow | null | undefined): string | null {
  const r = row?.result as { source?: unknown; finalRung?: unknown; placementRung?: unknown; total?: unknown } | null | undefined;
  if (!r || typeof r !== "object") return null;
  if (r.source !== RESULT_TAG[strand]) return null;
  // MA-1120 (RED r1 M1): the PLACEMENT rung (the module the start rule wrote),
  // falling back to finalRung for rows written before MA-1120
  const rung = diagnosticResultRung(r);
  if (rung === null || !(Number(r.total) > 0)) return null;
  return strand === "math" ? mathModuleIdForRung(rung) : readingModuleIdForRung(rung);
}

/** The newest row (rows are newest-first, as fetchPlacementResultsForStudent
 *  returns them; re-sorted defensively by created_at) tagged for `strand`. */
function newestRowFor(strand: ModuleStrand, rows: readonly PlacementResultRow[]): PlacementResultRow | null {
  const tagged = rows.filter((r) => (r?.result as { source?: unknown } | null | undefined)?.source === RESULT_TAG[strand]);
  if (tagged.length === 0) return null;
  const t = (r: PlacementResultRow) => (typeof r.created_at === "string" ? Date.parse(r.created_at) || 0 : 0);
  return [...tagged].sort((a, b) => t(b) - t(a))[0];
}

/**
 * Pure. Returns `prev` itself (same reference) when nothing is restored, else
 * a copy whose `moduleProvenance` gained an entry per restorable strand.
 *  - a strand with ANY local entry (current or stale) is never touched;
 *  - an unknown age restores nothing (isEvidenceFloor already reads evidence);
 *  - a floor equal to this age's own seed is age → not restored;
 *  - otherwise, the newest stored diagnostic of the strand resolving to
 *    exactly the child's current module → {moduleId, "diagnostic"};
 *  - MA-1226 slice 2 (D-1104-35, D-1104-37): `cloudCheckIn[strand]` gets the
 *    newest row's check-in facts (derived, device-local) whenever that row
 *    is NEWER (by completedAt) than this device's own stored result, or the
 *    device holds none, or the stored record is unusable (no module id);
 *    the evidence-floor rules then read whichever check-in is newer (a tie:
 *    the device's own). Rewritten only when it changed.
 */
export function planProvenanceRestore<P extends ProgressLike>(
  prev: P | null | undefined,
  rows: readonly PlacementResultRow[] | null | undefined,
): P | null | undefined {
  if (!prev || !Array.isArray(rows) || rows.length === 0) return prev;
  const age = prev.studentAge;
  if (typeof age !== "number" || !Number.isFinite(age)) return prev;
  const seeds = ageSeedModules(age, prev.parentPriors as Parameters<typeof ageSeedModules>[1]);
  if (!seeds.own) return prev;
  const out: ModuleProvenance = {};
  const checkIns: CloudCheckIns = {};
  for (const strand of STRANDS) {
    const current = strand === "math" ? prev.mathModuleId : prev.readingModuleId;
    if (!current) continue;
    const row = newestRowFor(strand, rows);
    const diag = diagnosticModuleFromRow(strand, row);
    if (!hasModuleProvenanceEntry(prev, strand)) {
      const own = strand === "math" ? seeds.own.mathModuleId : seeds.own.readingModuleId;
      if (current !== own && diag === current) out[strand] = { moduleId: current, source: "diagnostic" };
    }
    // MA-1226 slice 2 (D-1104-35, D-1104-37): the newest row's check-in facts
    // (its module, outcome, time, accuracy / total), so the evidence-floor
    // rules and the mission read here exactly what the device that ran the
    // NEWEST check-in reads, wherever it was run.
    const local = strand === "math" ? prev.placementResults : prev.readingPlacementResults;
    const hasLocal = !!local && typeof local === "object";
    const rec = diag ? cloudCheckInFromRow(diag, row) : null;
    const newer = !!rec && (!hasLocal || checkInTime(rec.completedAt) > checkInTime((local as { completedAt?: unknown }).completedAt));
    const unusable = !!prev.cloudCheckIn?.[strand] && !validCloudCheckIn(prev.cloudCheckIn[strand]);
    if (rec && (newer || unusable)) {
      if (JSON.stringify(prev.cloudCheckIn?.[strand] ?? null) !== JSON.stringify(rec)) checkIns[strand] = rec;
    }
  }
  if (!out.math && !out.reading && !checkIns.math && !checkIns.reading) return prev;
  return {
    ...prev,
    ...(out.math || out.reading ? { moduleProvenance: { ...(prev.moduleProvenance ?? {}), ...out } } : {}),
    ...(checkIns.math || checkIns.reading ? { cloudCheckIn: { ...(prev.cloudCheckIn ?? {}), ...checkIns } } : {}),
  };
}

/** MA-1226 slice 2 (D-1104-35): the check-in facts of a cloud row that
 *  resolved to `moduleId` (device-local derived state, no new cloud column). */
export function cloudCheckInFromRow(moduleId: string, row: PlacementResultRow | null | undefined): CloudCheckIn {
  const r = row?.result as { completedAt?: unknown; accuracy?: unknown; total?: unknown } | null | undefined;
  return {
    moduleId,
    noFailure: checkInOutcome(r),
    completedAt: typeof r?.completedAt === "string" ? r.completedAt : null,
    accuracy: typeof r?.accuracy === "number" ? r.accuracy : null,
    total: typeof r?.total === "number" ? r.total : null,
  };
}

/** D-1104-35 / D-1104-37: the cloud is read for the strand's check-in when
 *  - a stored record is unusable (no module id; RED r4 NIT-4), or
 *  - the current module is recorded as the diagnostic's, this device holds no
 *    stored result and no record was carried yet (a device restored before
 *    D-1104-35), or
 *  - (RED r4 MUST-E) the current module is a module a check-in rung can
 *    produce and no check-in this device knows of (its own stored result or
 *    the carried record) resolved to it: a newer check-in run on another
 *    device may have moved the levels this device merged. */
function needsCheckInRecord(prev: ProgressLike, strand: ModuleStrand): boolean {
  const rec = prev.cloudCheckIn?.[strand];
  if (rec && !validCloudCheckIn(rec)) return true;
  const local = strand === "math" ? prev.placementResults : prev.readingPlacementResults;
  const hasLocal = !!local && typeof local === "object";
  if (!hasLocal) return recordedModuleSource(prev, strand) === "diagnostic" && !rec;
  const current = strand === "math" ? prev.mathModuleId : prev.readingModuleId;
  if (!current || !DIAGNOSTIC_RUNG_MODULE_IDS[strand].includes(current)) return false;
  if (newestCheckIn(prev as Parameters<typeof newestCheckIn>[0], strand)?.moduleId === current) return false;
  return validCloudCheckIn(rec)?.moduleId !== current;
}

/** True when a read could change anything: some strand has no entry yet, or
 *  a diagnostic strand still lacks its check-in record (D-1104-35). */
export function needsProvenanceRestore(prev: ProgressLike | null | undefined): boolean {
  if (!prev) return false;
  return STRANDS.some((s) => !hasModuleProvenanceEntry(prev, s) || needsCheckInRecord(prev, s));
}

/**
 * Fail-safe restore for ONE child on load. `ctx` is the same child-scoped
 * context App.jsx hands the other pull-merge side effects (autoReadPullMerge):
 * { getActiveChildId, setProgressForChild, setProgress, loadStored,
 *   saveStored, isChildLinked }. Resolves to "restored" | "skipped" |
 * "unlinked" | "error"; never throws.
 */
export async function restoreProvenanceForChild(args: {
  studentId: string | null | undefined;
  localChildId: string | null | undefined;
  ctx: {
    getActiveChildId?: () => string | null;
    setProgressForChild?: (id: string, fn: (p: any) => any) => void;
    setProgress?: (fn: (p: any) => any) => void;
    loadStored: (id: string) => any;
    saveStored: (id: string, p: any) => void;
    isChildLinked?: (childId: string, studentId: string) => boolean;
  };
  fetchRows: (studentId: string) => Promise<readonly PlacementResultRow[]>;
}): Promise<"restored" | "skipped" | "unlinked" | "error"> {
  const { studentId, localChildId, ctx, fetchRows } = args;
  if (!studentId || !localChildId) return "skipped";
  try {
    // Skip the read when there is nothing to restore (both strands recorded).
    if (!needsProvenanceRestore(ctx.loadStored(localChildId))) return "skipped";
    const rows = await fetchRows(studentId);
    if (!Array.isArray(rows) || rows.length === 0) return "skipped";
    // The child may have been removed / re-linked since the read started.
    if (ctx.isChildLinked && !ctx.isChildLinked(localChildId, studentId)) return "unlinked";
    const transform = (prev: any) => planProvenanceRestore(prev, rows);
    const stored = ctx.loadStored(localChildId);
    const restorable = planProvenanceRestore(stored, rows) !== stored;
    const active = ctx.getActiveChildId ? ctx.getActiveChildId() : null;
    if (active === localChildId) {
      // The live, id-scoped setter saves under THIS child's key (App.jsx's
      // setProgressForChild); the updater re-plans on the freshest state.
      (ctx.setProgressForChild ? (fn: (p: any) => any) => ctx.setProgressForChild!(localChildId, fn) : ctx.setProgress!)(transform);
    } else if (restorable) {
      ctx.saveStored(localChildId, transform(stored));
    }
    return restorable ? "restored" : "skipped";
  } catch {
    return "error";
  }
}
